import styled from 'styled-components';

import {
    GetOrganizationQuery,
    OdooCompany,
    OrganizationPaymentTimeType,
    OrganizationPaymentType,
} from 'data/__generated__';

import { SectionColumn, SectionContainer, ColumnsSectionContainer } from 'components/DetailsView/Section';
import { DetailFormCheckbox, DetailFormSelect, DetailFormValue } from 'components/DetailsView';
import { PaymentType } from '../components/PaymentType';
import { ORGANIZATION_PAYMENT_TYPE_LABELS } from '../constants/payment-types';
import { useFormContext } from 'react-hook-form';

// Les encaissements Stripe (CB/SEPA) sont réconciliés sur le journal STRIPE TOTEM, rattaché à la société
// NSEDA → ces moyens de paiement ne sont proposés que pour les organisations NSEDA (cf. garde-fou backend).
const STRIPE_PAYMENT_TYPES = [OrganizationPaymentType.CreditCard, OrganizationPaymentType.SepaDirectDebit];

function getTranslatedOrganizationPaymentTimeType(paymentTimeType: OrganizationPaymentTimeType): string {
    switch (paymentTimeType) {
        case OrganizationPaymentTimeType.EndOfMonth:
            return 'Fin du mois';
        case OrganizationPaymentTimeType.NumberOfDay:
            return 'Fin de la journée';
    }
}

function getStripeLink(stripeId?: string | null): string {
    return 'https://dashboard.stripe.com/customers/' + stripeId;
}

export const PaymentInfoSection = ({ organization }: { organization: GetOrganizationQuery['organization'] }) => {
    const {
        hasDetailedOrderInvoicing,
        hasFreefoodIncluded,
        mandateSEPA,
        paymentTimeType,
        paymentType,
        hasOdooOrganizationParentInvoicing,
        hasPrepaidOrganizationCards,
        hasPrepaidSubventions,
        segregatedInvoicing,
        stripeId,
        odooInvoiceReference,
        odooCompany,
    } = organization;

    const { register } = useFormContext();

    // Non-NSEDA : on masque CB/SEPA, mais on conserve toujours la valeur déjà enregistrée pour ne pas
    // altérer une config existante (l'assainissement de l'existant est traité séparément, pas ici).
    const isNsedaOrganization = odooCompany === OdooCompany.Nseda;
    const paymentTypeOptions = Object.values(OrganizationPaymentType)
        .filter((value) => isNsedaOrganization || !STRIPE_PAYMENT_TYPES.includes(value) || value === paymentType)
        .map((value) => ({ value, label: ORGANIZATION_PAYMENT_TYPE_LABELS[value] }));

    return (
        <SectionContainer title="Informations de facturation" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={2}>
                <SectionColumn>
                    <PaymentType paymentType={paymentType} />
                    <DetailFormCheckbox
                        name="hasPrepaidOrganizationCards"
                        label="Ne pas facturer badges pro"
                        defaultChecked={hasPrepaidOrganizationCards}
                    />
                    <DetailFormCheckbox
                        name="hasPrepaidSubventions"
                        label="Ne pas facturer les subventions"
                        defaultChecked={hasPrepaidSubventions}
                    />
                    <DetailFormCheckbox
                        name="segregatedInvoicing"
                        label="Ne pas fusionner les factures (abonnement, conso, refacturation, freefood)"
                        defaultChecked={segregatedInvoicing}
                    />

                    <DetailFormCheckbox
                        name="hasFreefoodIncluded"
                        label="Freefood comprise dans le forfait (ne pas facturer les commandes)"
                        defaultChecked={hasFreefoodIncluded}
                    />
                    <DetailFormCheckbox
                        name="hasDetailedOrderInvoicing"
                        label="Détailler les commandes Freefood par produit sur la facture"
                        defaultChecked={hasDetailedOrderInvoicing}
                    />
                    <DetailFormSelect
                        label="Moyen de paiement"
                        placeholder="Sélectionner un moyen de paiement"
                        defaultValue={paymentType}
                        name="paymentType"
                        options={paymentTypeOptions}
                    />
                    <DetailFormSelect
                        label="Type de délai de paiement"
                        placeholder="Sélectionner un type de délai de paiement"
                        defaultValue={paymentTimeType}
                        name="paymentTimeType"
                        options={Object.values(OrganizationPaymentTimeType).map((value) => ({
                            value,
                            label: getTranslatedOrganizationPaymentTimeType(value),
                        }))}
                    />
                    <InfoText>
                        Pour consulter ou mettre à jour les moyens de paiement et les informations de facturation, merci
                        de consulter Stripe.
                    </InfoText>
                    <ExternalResourceLink
                        href={getStripeLink(stripeId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        $disabled={!stripeId}
                    >
                        <span>{stripeId ? 'Consulter les informations sur Stripe' : 'Pas de stripeId'}</span>
                    </ExternalResourceLink>
                    {mandateSEPA ? (
                        <MandateLink href={mandateSEPA} target="_blank" rel="noopener noreferrer">
                            Mandat SEPA
                        </MandateLink>
                    ) : (
                        <span>Pas de mandat SEPA</span>
                    )}
                </SectionColumn>
                <SectionColumn>
                    <DetailFormCheckbox
                        name="hasOdooOrganizationParentInvoicing"
                        label="Facturation sur l'organisation parente Odoo"
                        defaultChecked={hasOdooOrganizationParentInvoicing}
                    />
                    <DetailFormValue
                        defaultValue={odooInvoiceReference}
                        type="text"
                        {...register('odooInvoiceReference', { required: false })}
                        label="Référence de facture de Odoo"
                    />
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

const InfoText = styled.span`
    font-style: italic;
    color: ${({ theme }) => theme.textColor};
`;

const MandateLink = styled.a`
    display: flex;
    flex-direction: row;
    align-items: center;
    color: ${({ theme }) => theme.ctaPrimaryColor};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const ExternalResourceLink = styled.a<{ $disabled: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;

    color: ${({ theme }) => theme.ctaPrimaryColor};
    text-decoration: none;
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'default')};
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};

    &:hover {
        text-decoration: underline;
    }

    & > :not(:first-child) {
        margin-left: 10px;
    }
`;
